'use client';

import { useMemo, useState } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { alpha, Box, Button, Grid, IconButton, Popover, Typography, useTheme } from '@mui/material';
import {
  addMonths,
  endOfMonth,
  endOfToday,
  endOfYesterday,
  format,
  isSameDay,
  isWithinInterval,
  startOfMonth,
  startOfToday,
  startOfYesterday,
  subDays,
  subMonths,
} from 'date-fns';

interface DateRangePickerProps {
  onSelect: (start: number | undefined, end: number | undefined) => void;
  onClear?: () => void;
  height?: number | string;
}

const SHORTCUTS = [
  { label: 'Today', getValue: () => ({ start: startOfToday(), end: endOfToday() }) },
  { label: 'Yesterday', getValue: () => ({ start: startOfYesterday(), end: endOfYesterday() }) },
  {
    label: 'Last Week',
    getValue: () => ({ start: subDays(startOfToday(), 7), end: endOfToday() }),
  },
  {
    label: 'Last Month',
    getValue: () => ({ start: subMonths(startOfToday(), 1), end: endOfToday() }),
  },
  {
    label: 'Last 3 Months',
    getValue: () => ({ start: subMonths(startOfToday(), 3), end: endOfToday() }),
  },
  {
    label: 'Year to Date',
    getValue: () => ({ start: new Date(new Date().getFullYear(), 0, 1), end: endOfToday() }),
  },
  {
    label: 'Last Year',
    getValue: () => ({
      start: new Date(new Date().getFullYear() - 1, 0, 1),
      end: new Date(new Date().getFullYear() - 1, 11, 31),
    }),
  },
  { label: 'All', getValue: () => ({ start: undefined, end: undefined }) },
];

export function DateRangePicker({ onSelect, onClear, height = 40 }: DateRangePickerProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedRange, setSelectedRange] = useState<{ start?: Date; end?: Date }>({
    start: undefined,
    end: undefined,
  });
  const [activeShortcut, setActiveShortcut] = useState('All');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleShortcutClick = (shortcut: (typeof SHORTCUTS)[0]) => {
    const range = shortcut.getValue();
    setSelectedRange(range);
    setActiveShortcut(shortcut.label);
  };

  const handleDateClick = (date: Date) => {
    setActiveShortcut('');
    if (!selectedRange.start || (selectedRange.start && selectedRange.end)) {
      setSelectedRange({ start: date, end: undefined });
    } else {
      if (date < selectedRange.start) {
        setSelectedRange({ start: date, end: selectedRange.start });
      } else {
        setSelectedRange({ start: selectedRange.start, end: date });
      }
    }
  };

  const handleSetDates = () => {
    const startTimestamp = selectedRange.start
      ? Math.floor(new Date(selectedRange.start).setHours(0, 0, 0, 0) / 1000)
      : undefined;
    const endTimestamp = selectedRange.end
      ? Math.floor(new Date(selectedRange.end).setHours(23, 59, 59, 999) / 1000)
      : selectedRange.start
        ? Math.floor(new Date(selectedRange.start).setHours(23, 59, 59, 999) / 1000)
        : undefined;
    onSelect(startTimestamp, endTimestamp);
    handleClose();
  };

  const handleClear = () => {
    setSelectedRange({ start: undefined, end: undefined });
    setActiveShortcut('All');
    if (onClear) onClear();
    onSelect(undefined, undefined);
    handleClose();
  };

  const daysInMonth = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days: Date[] = [];
    let curr = start;
    // Padding for first week
    const startDay = start.getDay();
    for (let i = 0; i < startDay; i++) {
      days.push(subDays(start, startDay - i));
    }
    while (curr <= end) {
      days.push(curr);
      curr = subDays(curr, -1);
    }
    return days;
  }, [currentMonth]);

  const open = Boolean(anchorEl);

  return (
    <Box>
      <Button
        variant="outlined"
        onClick={handleClick}
        startIcon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: 'none',
          borderRadius: '8px',
          height: '38px',
          px: 2,
          fontWeight: 600,
          fontSize: '0.85rem',
          borderColor:
            theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.12)',
          color: theme.palette.text.primary,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'white',
          '&:hover': {
            bgcolor:
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            borderColor:
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        Date
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: 280, sm: 440 },
            height: { xs: 'auto', sm: 310 },
          }}
        >
          {/* Shortcuts sidebar */}
          <Box
            sx={{
              width: { xs: '100%', sm: 150 },
              borderRight: { xs: 'none', sm: '1px solid' },
              borderBottom: { xs: '1px solid', sm: 'none' },
              borderColor: 'divider',
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              flexWrap: 'wrap',
              gap: 0.5,
              py: 1,
              px: { xs: 1, sm: 0 },
            }}
          >
            {SHORTCUTS.map((s) => (
              <Button
                key={s.label}
                onClick={() => handleShortcutClick(s)}
                sx={{
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  px: 1.5,
                  py: 0.5,
                  textTransform: 'none',
                  color: activeShortcut === s.label ? 'primary.main' : 'text.primary',
                  fontWeight: activeShortcut === s.label ? 700 : 500,
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: 'action.hover' },
                  position: 'relative',
                }}
              >
                {s.label}
                {activeShortcut === s.label && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: { xs: 4, sm: 12 },
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                    }}
                  />
                )}
              </Button>
            ))}
            {(selectedRange.start || selectedRange.end) && (
              <Button
                onClick={handleClear}
                sx={{
                  mt: { xs: 0, sm: 'auto' },
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  px: 1.5,
                  py: 1,
                  textTransform: 'none',
                  color: 'error.main',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: 'rgba(224, 102, 79, 0.08)' },
                }}
              >
                Clear Dates
              </Button>
            )}
          </Box>

          {/* Calendar View */}
          <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
            >
              <Box sx={{ display: 'flex' }}>
                <IconButton
                  size="small"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 12))}
                >
                  <KeyboardDoubleArrowLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                >
                  <KeyboardArrowLeftIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                {format(currentMonth, 'MMMM yyyy')}
              </Typography>
              <Box sx={{ display: 'flex' }}>
                <IconButton
                  size="small"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                >
                  <KeyboardArrowRightIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 12))}
                >
                  <KeyboardDoubleArrowRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Grid */}
            <Grid container columns={7} spacing={0.25}>
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                <Grid item xs={1} key={d} sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem' }}
                  >
                    {d}
                  </Typography>
                </Grid>
              ))}
              {daysInMonth.map((day, i) => {
                const isSelected =
                  (selectedRange.start && isSameDay(day, selectedRange.start)) ||
                  (selectedRange.end && isSameDay(day, selectedRange.end));
                const inRange =
                  selectedRange.start &&
                  selectedRange.end &&
                  isWithinInterval(day, { start: selectedRange.start, end: selectedRange.end });
                const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

                return (
                  <Grid item xs={1} key={i}>
                    <Button
                      onClick={() => handleDateClick(day)}
                      sx={{
                        minWidth: 0,
                        width: '100%',
                        height: 28,
                        p: 0,
                        fontSize: '0.75rem',
                        fontWeight: isSelected ? 700 : 400,
                        borderRadius: inRange ? 0 : 1,
                        color: isSelected
                          ? 'white'
                          : isCurrentMonth
                            ? 'text.primary'
                            : 'text.disabled',
                        bgcolor: isSelected
                          ? 'primary.main'
                          : inRange
                            ? alpha(theme.palette.primary.main, 0.2)
                            : 'transparent',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                        },
                        ...(inRange &&
                          !isSelected && {
                            borderRadius: 0,
                          }),
                        ...(isSelected &&
                          selectedRange.start &&
                          isSameDay(day, selectedRange.start) &&
                          selectedRange.end && {
                            borderTopLeftRadius: '50%',
                            borderBottomLeftRadius: '50%',
                          }),
                        ...(isSelected &&
                          selectedRange.end &&
                          isSameDay(day, selectedRange.end) && {
                            borderTopRightRadius: '50%',
                            borderBottomRightRadius: '50%',
                          }),
                      }}
                    >
                      {day.getDate()}
                    </Button>
                  </Grid>
                );
              })}
            </Grid>

            {/* Footer */}
            <Box
              sx={{
                mt: 'auto',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleClose}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  fontSize: '0.8125rem',
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
                  whiteSpace: 'nowrap',
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSetDates}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  boxShadow: 'none',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
                  whiteSpace: 'nowrap',
                }}
              >
                Set Dates
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
